import {
  CHAT_SESSION_PAGINATION,
  CHAT_WINDOW_SIZES,
  UI_STATES,
} from '@/components/SearchAssistant/constants';
import {
  findMostRecentSession,
  normalizeSessions,
} from '@/components/SearchAssistant/helpers/sessionHelpers';

import { sortMessagesByCreatedAt } from '@/components/SearchAssistant/helpers/messageHelpers';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { createStoreSelectors } from './createStoreSelectors';

const createSessionStore = (set, get) => ({
  sessions: {},
  currentSessionId: null,
  creatingSession: false,
  sessionsPage: CHAT_SESSION_PAGINATION.DEFAULT_PAGE,
  hasMoreSessions: true,
  loadingMoreSessions: false,

  setCreatingSession: (isCreating) =>
    set({ creatingSession: isCreating }, false, 'setCreatingSession'),

  setLoadingMoreSessions: (loading) =>
    set({ loadingMoreSessions: loading }, false, 'setLoadingMoreSessions'),

  incrementSessionsPage: () =>
    set((state) => ({ sessionsPage: state.sessionsPage + 1 }), false, 'incrementSessionsPage'),

  setSessionsPage: (page) => set({ sessionsPage: Math.max(1, page) }, false, 'setSessionsPage'),

  setHasMoreSessions: (hasMore) => set({ hasMoreSessions: hasMore }, false, 'setHasMoreSessions'),

  storeAndSetCurrentSession: (session) => {
    const sessionData = normalizeSessions([session]);

    set(
      (state) => ({
        sessions: { ...state.sessions, ...sessionData },
        currentSessionId: session.id,
      }),
      false,
      'storeAndSetCurrentSession',
    );
  },

  setSessions: (sessions) => {
    const normalizedSessions = normalizeSessions(sessions);

    const state = get();

    set(
      {
        sessions: { ...state.sessions, ...normalizedSessions },
      },
      false,
      'setSessions',
    );

    if (!state.currentSessionId) {
      const mostRecentSession = findMostRecentSession(normalizedSessions);
      if (mostRecentSession) {
        set({ currentSessionId: mostRecentSession.id }, false, 'setCurrentSession');
      }
    }
  },

  selectSession: (sessionId) => {
    if (get().sessions[sessionId]) {
      set({ currentSessionId: sessionId }, false, 'selectSession');
    }
  },

  getCurrentSession: () => {
    const state = get();
    return state.currentSessionId ? state.sessions[state.currentSessionId] : null;
  },
});

const createMessageStore = (set) => ({
  messages: {},
  input: '',
  contentIsLoading: false,
  isAssistantTyping: {},
  setMessages: (currentSessionId, messages) =>
    set(
      (state) => ({
        messages: {
          ...state.messages,
          [currentSessionId]: sortMessagesByCreatedAt(messages),
        },
      }),
      false,
      'setMessages',
    ),
  addOptimisticMessage: (currentSessionId, message) =>
    set(
      (state) => {
        const existingMessages = state.messages[currentSessionId] || [];
        const optimisticMessage = {
          ...message,
          status: UI_STATES.PENDING,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          __typename: 'ChatMessage',
          chatSession: {
            __typename: 'ChatSession',
            id: currentSessionId,
          },
        };

        return {
          messages: {
            ...state.messages,
            [currentSessionId]: sortMessagesByCreatedAt([...existingMessages, optimisticMessage]),
          },
        };
      },
      false,
      'addOptimisticMessage',
    ),
  updateOptimisticMessage: (currentSessionId, optimisticId, actualMessage) =>
    set(
      (state) => {
        const existingMessages = state.messages[currentSessionId] || [];
        const updatedMessages = existingMessages.map((msg) =>
          msg.id === optimisticId ? { ...actualMessage, status: UI_STATES.DELIVERED } : msg,
        );

        return {
          messages: {
            ...state.messages,
            [currentSessionId]: sortMessagesByCreatedAt(updatedMessages),
          },
        };
      },
      false,
      'updateOptimisticMessage',
    ),
  removeOptimisticMessage: (currentSessionId, optimisticId) =>
    set(
      (state) => {
        const existingMessages = state.messages[currentSessionId] || [];
        const filteredMessages = existingMessages.filter((msg) => msg.id !== optimisticId);

        return {
          messages: {
            ...state.messages,
            [currentSessionId]: filteredMessages,
          },
        };
      },
      false,
      'removeOptimisticMessage',
    ),
  setInput: (input) => set({ input }, false, 'setInput'),
  setContentLoading: (contentIsLoading) => set({ contentIsLoading }, false, 'setContentLoading'),
  setAssistantTyping: (sessionId, isTyping) =>
    set(
      (state) => ({
        isAssistantTyping: {
          ...state.isAssistantTyping,
          [sessionId]: isTyping,
        },
      }),
      false,
      'setAssistantTyping',
    ),
});

const defaultState = {
  isOpen: false,
  position: { x: 50, y: 50 },
  size: { width: CHAT_WINDOW_SIZES.WIDTH, height: CHAT_WINDOW_SIZES.HEIGHT },
  hasBeenOpened: false,
  isDragging: false,
  isMobile: false,
  renderTriggerButton: false,
};

const createUIStore = (set) => ({
  ...defaultState,

  toggleOpen: () =>
    set((state) => ({ isOpen: !state.isOpen, hasBeenOpened: true }), false, 'toggleOpen'),
  setOpen: (isOpen) => set({ isOpen }, false, 'setOpen'),
  setPosition: (position) => set({ position }, false, 'setPosition'),
  setSize: (size) => set({ size }, false, 'setSize'),
  setDragging: (isDragging) => set({ isDragging }, false, 'setDragging'),
  updateResponsiveSize: (isMobile) => {
    const width = isMobile ? CHAT_WINDOW_SIZES.MOBILE_WIDTH : CHAT_WINDOW_SIZES.WIDTH;
    set(
      { size: { width, height: CHAT_WINDOW_SIZES.HEIGHT }, isMobile },
      false,
      'updateResponsiveSize',
    );
  },
  setRenderTriggerButton: (renderTriggerButton) =>
    set({ renderTriggerButton }, false, 'setRenderTriggerButton'),
});

const createSidebarStore = (set) => ({
  isSidebarOpen: false,
  toggleSidebar: () =>
    set((state) => ({ isSidebarOpen: !state.isSidebarOpen }), false, 'toggleSidebar'),
  setSidebarOpen: (isOpen) => set({ isSidebarOpen: isOpen }, false, 'setSidebarOpen'),
});

// Separate UI Store
const searchAssistantUIStoreBase = create(
  devtools(
    (set, get) => ({
      ...createUIStore(set, get),
      ...createSidebarStore(set, get),
    }),
    {
      name: 'ai-assistant-ui',
    },
  ),
);

// Main Store (sessions and messages)
const searchAssistantStoreBase = create(
  devtools(
    (set, get) => ({
      ...createSessionStore(set, get),
      ...createMessageStore(set, get),
    }),
    {
      name: 'ai-assistant',
    },
  ),
);

export const searchAssistantUIStore = createStoreSelectors(searchAssistantUIStoreBase);
export const searchAssistantStore = createStoreSelectors(searchAssistantStoreBase);
